import { useSearchParams } from 'react-router-dom'
import { useRef, useState } from 'react'
import entreNational from '../assets/landing/entre-national.jpg'
import entreSchool from '../assets/landing/entre-school.jpg'
import entreWorkshop from '../assets/landing/entre-workshop.jpg'
import entreContest from '../assets/landing/entre-contest.jpg'
import entreStep1 from '../assets/landing/entre-step1.jpg'
import entreStep2 from '../assets/landing/entre-step2.jpg'
import entreStep3 from '../assets/landing/entre-step3.jpg'
import GoogleOneTap from '../components/GoogleOneTap'
import TermsDialog from '../components/TermsDialog'
import LoginDialog from '../components/LoginDialog'
import SiteHeader from '../components/landing/SiteHeader'
import type { NavItem } from '../components/landing/SiteHeader'
import { useSmoothScroll, useHeaderOnLight, useManifesto, useScrollGallery, useReveal } from '../components/landing/effects'
import { useRedirectIfSignedIn } from '../hooks/useRedirectIfSignedIn'
import './landingSite.css'
import './EntrePage.css'

const CONTACT_URL = 'https://mirasso.co.jp/contact'
const COMPANY_URL = 'https://mirasso.co.jp/company'

const NAV_ITEMS: NavItem[] = [
  { href: '/#problem', label: '課題' },
  { href: '/#product', label: 'プロダクト' },
  { href: '/#teacher', label: '先生向け' },
  { href: '/#curriculum', label: 'カリキュラム' },
  { href: '/#proof', label: '導入実績' },
  { href: '/#price', label: '料金' },
  { href: '/entre', label: 'アントレ部' },
  { href: COMPANY_URL, label: '会社概要', external: true },
]

const GALLERY = [
  { img: entreSchool, tab: '校内でのMiraPASS実施', title: '校内での探究が、すべての土台になる。', caption: '校内でのMiraPASS実施', alt: '校内でMiraPASSに取り組む生徒たち', eager: true },
  { img: entreWorkshop, tab: '月1回の合同ワークショップ', title: '起業家や他校の生徒と、オンラインで壁打ち。', caption: '月1回の合同ワークショップ', alt: '全国の生徒と起業家がオンラインでつながる合同ワークショップ' },
  { img: entreNational, tab: '全国合同ワークショップ', title: '画面の向こうに、全国の仲間がいる。', caption: '全国合同ワークショップ', alt: '全国の学校の生徒がオンラインでつながる合同ワークショップの様子' },
  { img: entreContest, tab: 'コンテストへの挑戦', title: '挑戦の経験は、進路の物語になる。', caption: 'コンテストへの挑戦', alt: '企画を発表する生徒たち' },
]

const MANIFESTO = [
  { t: 'アントレ部は、' }, { t: '部活動じゃない。' },
  { t: '自分の「好き」を、' }, { t: '学校の外の大人や、' },
  { t: '全国の仲間にぶつけていく場所。' },
  { t: '一歩踏み出す勇気が、' }, { t: '次の一歩を連れてくる。' },
  { t: '挑戦の物語を、', accent: true }, { t: 'ここから。', accent: true },
]

const STEPS = [
  { no: '01', title: '校内でMiraPASSを実施', desc: '毎週、動画とAIみらいワークで自分のテーマを深めます。', img: entreStep1, alt: '校内でMiraPASSに取り組む生徒たち' },
  { no: '02', title: '月1回の合同ワークショップ', desc: '起業家や他校の生徒とオンラインで壁打ち。互いの探究を磨き合います。', img: entreStep2, alt: '全国の生徒と起業家がオンラインでつながる合同ワークショップ' },
  { no: '03', title: 'コンテストに挑戦', desc: '企画を形にし、発表・コンテストへ。挑戦の経験が進路にもつながります。', img: entreStep3, alt: '企画を発表する生徒たち' },
]

const VALUES = [
  { title: '全国の学校とつながる実践コミュニティ', desc: '地域も学校も越えて、同じ志の仲間と出会えます。' },
  { title: '実践者からの刺激', desc: '起業家など、社会で挑戦する大人の話に触れながら、自分の一歩を考えます。' },
  { title: '挑戦の成果が、見える形で残る', desc: 'コンテストへの挑戦や共創の経験は、総合型選抜などの進路にもつながります。' },
]

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" stroke="#0a6cf5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const EntrePage = () => {
  const [searchParams] = useSearchParams()
  const returnUrl = searchParams.get('returnUrl')
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false)
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(searchParams.get('login') === 'true')
  const rootRef = useRef<HTMLDivElement>(null)

  useRedirectIfSignedIn(returnUrl)
  useSmoothScroll()
  useHeaderOnLight(rootRef, '.sec.alt')
  useManifesto(rootRef)
  useScrollGallery(rootRef)
  useReveal(rootRef)

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

  return (
    <>
      <div className="mp-site mp-entre" ref={rootRef}>
        <SiteHeader logoHref="/" navItems={NAV_ITEMS} onLogin={() => setIsLoginDialogOpen(true)} />

        {/* ============ HERO ============ */}
        <section className="hero">
          <div className="bg" aria-hidden="true" style={{ backgroundImage: `url(${entreNational})` }} />
          <div className="veil" aria-hidden="true" />
          <div className="aurora" aria-hidden="true" />
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <p className="k">アントレ部</p>
            <h1>学校を越えて、<br /><span className="grad-text">挑戦がつながる。</span></h1>
            <p>アントレ部は、意欲的な生徒のために、全国の生徒とつながり、実践・挑戦の場を広げる学校横断の部活動です。</p>
            <span className="chip">MiraPASS標準プランに含まれます</span>
          </div>
        </section>

        {/* ============ GALLERY：アントレ部のある風景 ============ */}
        <section className="gal">
          <div className="gal-sticky">
            <div className="wrap">
              <div className="gal-tabs reveal" role="tablist" aria-label="ギャラリーの切り替え">
                {GALLERY.map((g, i) => (
                  <button key={g.tab} type="button" className={`gal-tab${i === 0 ? ' on' : ''}`}>{g.tab}</button>
                ))}
              </div>
            </div>
            <div className="gal-track">
              {GALLERY.map((g, i) => (
                <div key={g.tab} className={`gal-card${i === 0 ? ' active' : ''}`}>
                  <img src={g.img} alt={g.alt} loading={g.eager ? 'eager' : 'lazy'} fetchPriority={g.eager ? 'high' : undefined} />
                  <div className="cap"><b>{g.title}</b><span>{g.caption}</span></div>
                </div>
              ))}
            </div>
            <div className="gal-dots" aria-hidden="true">
              {GALLERY.map(g => <button key={g.tab} type="button" className="gal-dot" />)}
            </div>
          </div>
        </section>

        {/* ============ MANIFESTO：アントレ部の想い ============ */}
        <section className="manifesto" aria-label="アントレ部の想い">
          <div className="stickybox">
            <div className="mglow" aria-hidden="true" />
            <p className="mtext">
              {MANIFESTO.map((p, i) => (
                <span key={p.t}>
                  {i > 0 && ' '}
                  <span className={`w${p.accent ? ' accent' : ''}`}>{p.t}</span>
                </span>
              ))}
            </p>
          </div>
        </section>

        {/* ============ 活動の流れ ============ */}
        <section className="sec dark">
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <div className="reveal">
              <h2>活動の流れ</h2>
              <p className="sub">校内の探究から、全国の仲間との共創、そしてコンテストへの挑戦まで。挑戦の階段を、段階的にのぼれます。</p>
            </div>
            <div className="fsteps stagger">
              {STEPS.map(step => (
                <div className="fstep" key={step.no}>
                  <div className="fnum grad-text">{step.no}</div>
                  <div className="fbody">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <div className="fphoto"><img src={step.img} alt={step.alt} loading="lazy" /></div>
                </div>
              ))}
            </div>
            <div className="photo-duo reveal">
              <img src={entreNational} alt="全国の学校の生徒がオンラインでつながる合同ワークショップの様子" loading="lazy" />
              <div className="t">
                <b>画面の向こうに、全国の仲間がいる。</b>
                <p>月1回の合同ワークショップでは、地域も学校も越えて生徒同士がつながり、互いの探究に刺激を与え合います。校内だけでは生まれない「本気の空気」が、ここにあります。</p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ アントレ部の価値 ============ */}
        <section className="sec alt">
          <div className="wrap">
            <div className="reveal">
              <h2>アントレ部の価値</h2>
              <p className="sub">「校内だけ」では出会えない刺激が、生徒の意欲に火を灯します。</p>
            </div>
            <div className="pts stagger">
              {VALUES.map(v => (
                <div className="pt" key={v.title}>
                  <span className="ic"><CheckIcon /></span>
                  <div><b>{v.title}</b><p>{v.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section className="cta">
          <div className="grain" aria-hidden="true" />
          <div className="wrap reveal">
            <h2>アントレ部の詳細は、資料で。</h2>
            <p>参加校の事例や年間スケジュールなど、詳しい資料をお送りします。</p>
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">資料請求（無料）</a>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer>
          <div className="wrap">
            <div className="links">
              <a href="/">MiraPASSトップ</a>
              <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">会社概要</a>
              <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
              <button type="button" onClick={() => setIsTermsDialogOpen(true)}>利用規約</button>
            </div>
            <div>© 2025 MiraPASS All Rights Reserved.</div>
          </div>
        </footer>
      </div>

      <LoginDialog isOpen={isLoginDialogOpen} onClose={() => setIsLoginDialogOpen(false)} />
      <TermsDialog isOpen={isTermsDialogOpen} onClose={() => setIsTermsDialogOpen(false)} />

      {googleClientId && (
        <GoogleOneTap
          clientId={googleClientId}
          onSuccess={() => console.log('🟢 Google One Tap successful')}
          onError={(error) => console.error('🔴 Google One Tap error:', error)}
        />
      )}
    </>
  )
}

export default EntrePage
